<template>
  <div class="disqualified-list h-100">
    <!-- Section Title -->
    <div class="section-title-wrapper mb-4">
      <div class="section-title">
        <h2 class="text-h5 font-weight-medium">{{ title }}</h2>
        <div class="section-bar"></div>
      </div>
    </div>

    <!-- Loading Animation -->
    <v-overlay v-if="LoadingFlag" absolute class="loading-overlay">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-overlay>

    <!-- No Nominees Message -->
    <div v-else-if="!nominees.length" class="no-nominees">
      <v-icon size="64" color="grey lighten-1">mdi-account-multiple</v-icon>
      <p class="mt-4">No nominees found for this category</p>
    </div>

    <!-- Nominees Grid -->
    <div v-else class="nominees-scroll">
      <v-row class="nominees-container">
      <v-col
        v-for="(item, index) in nominees"
        :key="index"
        cols="12"
        sm="12"
        md="12"
        class="nominee-col"
        v-scroll-reveal="{
          delay: 200 * index,
          distance: '50px',
          duration: 800,
          origin: 'bottom',
        }"
      >
        <v-hover v-slot="{ hover }">
          <v-card
            class="disqualified-card nomination-card"
            :elevation="hover ? 8 : 3"
            :class="{ 'on-hover': hover }"
          >
            <v-card-title class="card-header pa-4">
              <v-avatar size="80" class="nominee-avatar elevation-2">
                <v-img
                  :src="item.MemberImage || defaultAvatarUrl"
                  :alt="item.MemberName"
                  @error="handleImageError"
                >
                  <template v-slot:placeholder>
                    <v-avatar color="primary" size="80">
                      <span class="white--text text-h4">{{
                        getInitials(item.MemberName)
                      }}</span>
                    </v-avatar>
                  </template>
                </v-img>
              </v-avatar>

              <div class="nominee-info ml-4">
                <h3 class="nominee-name text-h6">{{ item.MemberName }}</h3>
                <p class="organization-name caption mb-0">{{ item.LomName }}</p>
              </div>
            </v-card-title>

            <div class="card-action-buttons">
              <v-tooltip bottom v-if="ShowDisqualifyButton">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    :loading="disqualifyLoadingMap[item.MemberId] || false"
                    :disabled="item.Disqualified"
                    small
                    depressed
                    class="action-button disqualify-button"
                    @click.prevent="onDisqualifyClick(item)"
                  >
                    <v-icon left>mdi-account-check</v-icon>
                    <span class="button-text">DISQUALIFY</span>
                  </v-btn>
                </template>
                <span>Disqualify</span>
              </v-tooltip>
            </div>
          </v-card>
        </v-hover>
      </v-col>
      </v-row>
    </div>
    <!-- Disqualify Dialog -->
    <v-dialog v-model="disqualifyDialog" max-width="50%">
      <nomination-disqualified
        pageDescription="Disqualify Candidate"
        :showDialog="disqualifyDialog"
        @hideDialog="hideDisqualifyDialog($event)"
        :row="selectedData"
        v-if="disqualifyDialog"
      />
    </v-dialog>
  </div>
</template>

<script>
import companyConfig from "@/company_config.json";
import NominationDisqualified from "./NominationDisqualified.vue";

export default {
  components: { NominationDisqualified },
  name: "DisqualifiedCandidates",
  props: {
    year: {
      type: Number,
      required: true,
    },
    zone: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    designation: {
      type: Number,
      required: true,
    },
    type: {
      type: Number,
      required: true,
    },
    enableDisqualify: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      nominees: [],
      LoadingFlag: false,
      selectedData: null,
      disqualifyDialog: false,
      ShowDisqualifyButton: false,
      // per-item loading indicator for Disqualify action
      disqualifyLoadingMap: {},
    };
  },
  methods: {
    getInitials(name) {
      return name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .substr(0, 2);
    },
    handleImageError(error) {
      error.target.src = this.defaultAvatarUrl;
    },
    hideDisqualifyDialog(flag) {
      console.log("hideDisqualifyDialog called");
      console.log({ flag });
      this.disqualifyDialog = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    openDisqualifyDialog(tr) {
      console.log("openDisqualifyDialog called");
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.disqualifyDialog = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to disqualify";
        this.toast("error", message);
      }
    },
    onDisqualifyClick(item) {
      // show per-item loading momentarily while opening dialog
      if (item && item.MemberId)
        this.$set(this.disqualifyLoadingMap, item.MemberId, true);
      // preserve existing behavior
      this.openDisqualifyDialog(item);
      // clear loading after small delay so user sees the spinner
      setTimeout(() => {
        if (item && item.MemberId)
          this.$set(this.disqualifyLoadingMap, item.MemberId, false);
      }, 450);
    },
    async getNomineesData() {
      console.log(
        "getNomineesData called for designation: " + this.designation
      );

      try {
        const server_url = companyConfig.apiURL;
        const token = this.$session.get("token") || "";
        const add_url = server_url + "api/nomination/lists";
        const upload = {
          UserInterface: 6,
          Designation: this.designation,
          Zone: this.zone,
          Year: this.year,
        };

        const response = await this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (response.data.flag == 1) {
          this.nominees = response.data.records.TableData;
          this.LoadingFlag = true;
          this.ResultFlag = true;

          // Set visibility flags based on props
          this.ShowDisqualifyButton = this.enableDisqualify === true; // Always show disqualify button
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.LoadingFlag = false;
      }
    },
  },
  created() {
    // initialize visibility from prop so buttons appear immediately
    this.ShowDisqualifyButton = this.enableDisqualify === true;
    this.getNomineesData();
  },
  watch: {
    enableDisqualify(newVal) {
      this.ShowDisqualifyButton = newVal === true;
    },
    year() {
      this.getNomineesData();
    },
    zone() {
      this.getNomineesData();
    },
  },
};
</script>

<style lang="scss" scoped>
.disqualified-list {
  position: relative;
  min-height: 200px;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  background: linear-gradient(
    to bottom,
    rgba(245, 247, 250, 0.5),
    rgba(245, 247, 250, 0)
  );
  border-radius: 12px;

  .loading-overlay {
    backdrop-filter: blur(3px);
  }

  .section-title-wrapper {
    padding: 0;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    padding-bottom: 1rem;
  }

  .section-title {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    animation: slideInFromLeft 0.6s ease-out;

    h2 {
      color: #2c3e50;
      margin-bottom: 0.5rem;
      letter-spacing: -0.5px;
      font-weight: 600;
    }

    .section-bar {
      height: 3px;
      width: 40px;
      background: linear-gradient(to right, #2c63ac, #3498db);
      border-radius: 1.5px;
      animation: expandWidth 0.8s ease-out;
    }
  }

  .nominees-container {
    flex: 1;
    overflow-y: auto;
    margin: 0;
    max-height: calc(100vh - 300px);
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.2);
      border-radius: 3px;
    }
  }

  .nominees-scroll {
    flex: 1;
    overflow: auto;
    max-height: calc(100vh - 260px);
    padding-right: 4px;
  }

  .no-nominees {
    text-align: center;
    padding: 3rem;
    color: #95a5a6;
    animation: fadeIn 0.5s ease-out;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .nominee-col {
    transition: transform 0.3s ease-out;
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .nomination-card {
    border-radius: 12px !important;
    overflow: hidden;
    position: relative;
    margin-bottom: 1.5rem;
    border: none;
    background-color: white;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &.on-hover {
      transform: translateY(-8px);
    }

    .card-header {
      background: linear-gradient(135deg, #f5f7fa 0%, #f8f9fb 100%);
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    .nominee-avatar {
      border: 3px solid white;
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
      transition: transform 0.3s ease;

      .v-avatar {
        background: linear-gradient(135deg, #2c63ac, #3498db);
      }
    }

    &:hover .nominee-avatar {
      transform: scale(1.05);
    }

    .nominee-info {
      .nominee-name {
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 4px;
      }

      .organization-name {
        color: #7f8c8d;
        font-size: 0.875rem;
      }
    }

    .votes-badge {
      position: absolute;
      top: 1rem;
      right: 1rem;
      transition: transform 0.3s ease;

      &.badge-animated {
        transform: scale(1.1);
      }

      .votes-chip {
        font-weight: 600;
        letter-spacing: 0.5px;
      }
    }

    .status-section {
      background-color: #ffffff;
    }

    .action-btn {
      display: flex !important;
      align-items: center;
      justify-content: center;
      border-radius: 4px !important;
      font-size: 0.8rem !important;
      font-weight: 600 !important;
      letter-spacing: 0.4px;
      flex: 1;
      min-width: auto !important;
      text-transform: none !important;

      .v-icon {
        font-size: 22px;
      }

      .button-text {
        white-space: nowrap;
        font-weight: 700;
      }

      &.pdf-button {
        background-color: #e74c3c !important;
      }

      &.score-button {
        background-color: #009688 !important;
      }

      &.age-proof-button {
        background-color: #1976d2 !important;
      }

      .button-icon {
        margin-right: 4px;

        .v-icon {
          font-size: 16px;
        }
      }
    }

    .application-button {
      background: linear-gradient(135deg, #e53935, #c62828);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.1);

      &:hover {
        background: linear-gradient(135deg, #d32f2f, #b71c1c);
      }

      .v-icon {
        color: white;
        font-size: 18px;
      }
    }

    .score-button {
      background: linear-gradient(135deg, #00a173, #00896c);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.1);

      &:hover {
        background: linear-gradient(135deg, #009688, #007d6e);
      }

      .v-icon {
        color: white;
        font-size: 18px;
      }
    }

    .age-proof-button {
      background: linear-gradient(135deg, #1976d2, #1565c0);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.1);
      transition: all 0.3s ease;

      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #1565c0, #0d47a1);
        transform: translateY(-2px);
      }

      &:disabled {
        opacity: 0.7;
        cursor: default;
      }

      &.verified {
        background: linear-gradient(135deg, #2e7d32, #1b5e20);

        &:hover {
          background: linear-gradient(135deg, #2e7d32, #1b5e20);
        }
      }

      .v-icon {
        color: white;
        font-size: 18px;
        transition: transform 0.3s ease;
      }

      &:hover:not(:disabled) .v-icon {
        transform: scale(1.1);
      }
    }

    .score-button {
      &:disabled {
        background: linear-gradient(135deg, #4caf50, #388e3c);
        opacity: 0.7;
        cursor: default;
      }
    }
    /* Disqualified card specific styles */
    .disqualified-card {
      border-radius: 12px;
      overflow: hidden;
      transition: transform 260ms ease, box-shadow 260ms ease;
      background: linear-gradient(180deg, #ffffff, #fbfdff);
      border: 1px solid rgba(34, 38, 64, 0.04);
      padding-bottom: 12px;
    }

    .disqualified-card.on-hover {
      transform: translateY(-8px);
      box-shadow: 0 12px 30px rgba(31, 45, 61, 0.06);
    }

    .card-header {
      align-items: center;
      display: flex;
      gap: 12px;
      padding: 16px;
      background: linear-gradient(90deg, #f6f8ff, #eef6ff);
      border-bottom: 1px solid rgba(34, 38, 64, 0.04);
    }

    .card-action-buttons {
      display: flex;
      gap: 10px;
      padding: 12px 16px 8px 16px;
    }

    .disqualify-btn {
      min-width: 140px;
      height: 40px;
      box-shadow: 0 6px 18px rgba(79, 70, 229, 0.06);
      transition: transform 180ms ease, box-shadow 180ms ease;
    }

    .disqualify-btn:hover {
      transform: translateY(-3px) scale(1.01);
      box-shadow: 0 14px 36px rgba(79, 70, 229, 0.12);
    }
    .disqualify-button {
      min-width: 90px;
      height: 36px;
      border-radius: 8px;
      font-weight: 700;
      letter-spacing: 0.4px;
      background-color: #009688 !important;
      transition: transform 160ms ease, box-shadow 160ms ease;
    }

    .disqualify-button:hover:not(:disabled) {
      transform: translateY(-3px);
      box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
    }
    #disqualified-candidates {
      .page-header-section {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 24px;
        background-color: #3f51b5;
        border-radius: 4px 4px 0 0;
      }

      .header-title-container {
        .page-title {
          color: white;
          margin-bottom: 0;
          font-size: 18px;
          letter-spacing: 0.5px;
        }
      }

      .nominations-container {
        padding: 16px 24px;
      }

      .nominations-counter {
        color: #546e7a;
      }

      .nomination-card {
        border-radius: 8px;
        overflow: hidden;
        transition: all 0.3s ease;
        border: 1px solid #e0e0e0;
        background-color: white;

        &:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
          transform: translateY(-2px);
        }
      }

      // New Card Styling
      .candidate-header {
        background-color: #f5f5f5;
        display: flex;
        align-items: center;
        border-bottom: 1px solid #eaeaea;
      }

      .card-title {
        font-size: 16px !important;
        color: #333;
      }

      .votes-chip {
        height: 24px;
      }

      .candidate-body {
        padding: 16px;
      }

      .nominee-name {
        font-size: 14px;
        font-weight: 500;
        color: #333;
        margin-top: 6px;
        line-height: 1.2;
      }

      .nominee-id {
        font-size: 12px;
        margin-top: 2px;
      }

      .status-list {
        padding: 0;
      }

      .status-item {
        padding: 6px 0;
      }

      .status-label {
        color: #455a64;
      }

      .action-buttons {
        background-color: #fafafa;
        border-top: 1px solid #f0f0f0;
      }

      // Aadhaar section styling
      .aadhaar-name {
        line-height: 1.3;
        font-weight: 500;
        margin-top: 8px;
        margin-bottom: 5px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .aadhaar-details {
        padding: 15px;
        background-color: #f8f9fa;
        border-radius: 8px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
        margin-top: 4px;
        border: 1px solid #e0e0e0;
      }

      .detail-row {
        display: flex;
        padding: 4px 0;
        align-items: flex-start;
      }

      .detail-label {
        min-width: 100px;
        font-weight: 600;
        color: #3f51b5;
      }

      .detail-value {
        font-weight: 500;
        line-height: 1.4;
      }

      /* Format Aadhaar number for better readability */
      .detail-row:first-child .detail-value {
        letter-spacing: 1px;
        font-family: monospace;
        background: #f5f5f5;
        padding: 3px 8px;
        border-radius: 3px;
        font-size: 0.85rem;
        color: #333;
        border: 1px solid #e0e0e0;
      }

      .detail-row:last-child {
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        flex-direction: column;
      }

      /* Enhanced address styling */
      .detail-value[style*="word-break: break-word"] {
        padding: 6px 8px;
        background-color: white;
        border-radius: 4px;
        border: 1px solid #e0e0e0;
        font-size: 0.85rem;
        line-height: 1.5;
        color: #424242;
        font-weight: 400;
        margin-top: 4px;
        box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
      }

      .aadhaar-not-verified {
        background-color: #fff8e1;
        border-radius: 4px;
        margin-top: 8px;
        border: 1px solid rgba(255, 143, 0, 0.2);
      }

      .not-verified-message {
        color: #ff8f00;
        display: flex;
        align-items: center;
      }

      // Original styling
      .card-top-section {
        background-color: #f5f5f5;
        position: relative;
      }

      .card-org-name {
        font-size: 16px;
        font-weight: 500;
        color: #333;
      }

      .votes-badge {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 1;
      }

      .votes-count {
        font-size: 12px;
        font-weight: 500;
      }

      .card-main-content {
        padding: 16px;
      }

      .nominee-section {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
      }

      .nominee-photo-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 40%;
      }

      .nominee-details {
        text-align: center;
        margin-top: 8px;
      }

      .status-section {
        width: 60%;
      }

      .status-row {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        justify-content: space-between;
      }

      .status-chip {
        font-size: 11px;
      }

      .card-action-buttons {
        display: flex;
        justify-content: flex-start;
        flex-wrap: wrap;
      }

      .aadhaar-details-container {
        background-color: #f9f9f9;
        border-top: 1px solid #eee;
      }

      .nominee-info-header {
        background: #e8eaf6;
        padding: 6px 12px;
        border-radius: 4px;
        margin-bottom: 12px;
        display: flex;
        align-items: center;
        border-left: 3px solid #3f51b5;
      }

      .info-icon {
        color: #3f51b5;
      }
    }
    .status-item {
      .status-label {
        color: #7f8c8d;
        font-size: 0.875rem;
      }

      .status-chip {
        transition: transform 0.2s ease;

        &:hover {
          transform: scale(1.05);
        }
      }
    }
  }
}

@keyframes slideInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes expandWidth {
  from {
    width: 0;
  }
  to {
    width: 40px;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
